import { useId, useState } from 'react';
import { useColorMode } from '@chakra-ui/react';
import { t } from 'i18next';
import { useConvertEnergy } from '../hooks/useConvertEnergy';

interface EnergyHeartProps {
  count: number; // 0 ~ 5
  onReceiveClick?: () => void;
  energyPerPoint: number;
}

const EnergyHeart = ({ count, onReceiveClick, energyPerPoint }: EnergyHeartProps) => {
  const { colorMode } = useColorMode();
  const isFull = count >= 5;
  const uniqueId = useId();
  const [isPressed, setIsPressed] = useState(false);

  const { handleConvertEnergy, plusOneRef, isConvertingEnergy } = useConvertEnergy();

  const safeCount = Math.floor(Math.max(0, Math.min(count, 5)));

  const getLevelImage = (cnt: number) => {
    switch (cnt) {
      case 1:
        return '/assets/freeCharge/heart_level_1.png';
      case 2:
        return '/assets/freeCharge/heart_level_2.png';
      case 3:
        return '/assets/freeCharge/heart_level_3.png';
      case 4:
        return '/assets/freeCharge/heart_level_4.png';
      default:
        return null;
    }
  };

  const getLevelClass = (cnt: number) => {
    switch (cnt) {
      case 1:
        return 'bottom-[-7px]';
      case 2:
        return 'bottom-[4px] left-[5px] right-[5px]';
      case 3:
        return 'bottom-[-2px] left-[-6px] right-[-5px]';
      case 4:
        return 'bottom-[-2px] left-[-6px] right-[-6px]';
      default:
        return '';
    }
  };

  const levelImageSrc = getLevelImage(safeCount);

  const HEART_PATH_D =
    'M81.9614 144C82.9906 144 84.1484 143.692 85.4349 143.077C86.7729 142.462 88.0336 141.769 89.2172 141C103.626 131.769 116.105 122.077 126.654 111.923C137.255 101.769 145.463 91.3077 151.278 80.5385C157.093 69.7692 160 58.9231 160 48C160 40.9231 158.842 34.4615 156.526 28.6154C154.262 22.7179 151.123 17.641 147.109 13.3846C143.095 9.12821 138.413 5.84615 133.061 3.53846C127.709 1.17949 121.946 0 115.77 0C108.103 0 101.413 1.94872 95.7011 5.84615C90.0406 9.69231 85.4607 14.7692 81.9614 21.0769C78.5651 14.8205 74.0109 9.74359 68.2989 5.84615C62.5868 1.94872 55.8971 0 48.2296 0C42.0544 0 36.2909 1.17949 30.9391 3.53846C25.6388 5.84615 20.956 9.12821 16.8906 13.3846C12.8768 17.641 9.71202 22.7179 7.39634 28.6154C5.13211 34.4615 4 40.9231 4 48C4 58.9231 6.88174 69.7692 12.6452 80.5385C18.4602 91.3077 26.668 101.769 37.2687 111.923C47.9208 122.077 60.4513 131.769 74.86 141C75.9921 141.769 77.2271 142.462 78.5651 143.077C79.903 143.692 81.0351 144 81.9614 144Z';

  return (
    <div className="relative w-[156px] h-[144px] flex justify-center items-center select-none overflow-visible">
      {isFull ? (
        <>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 -10 164 162"
            className="absolute z-0 w-full h-full scale-100 origin-center glowPulse pointer-events-none overflow-visible"
            style={{
              transform: isPressed ? 'scale(0.96)' : 'scale(1)',
              transition: 'transform 120ms ease-out'
            }}
            aria-hidden="true"
          >
            <path d={HEART_PATH_D} fill="#FFFFFF" fillOpacity={0.7} />
          </svg>

          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 -10 164 162"
            fill="none"
            className="absolute z-10 w-full h-full scale-100 origin-center overflow-visible"
            style={{
              transform: isPressed ? 'scale(0.96)' : 'scale(1)',
              transition: 'transform 120ms ease-out'
            }}
            aria-label="Full Heart Icon"
            role="img"
          >
            <g filter={`url(#filter_full_${uniqueId})`}>
              <path
                d={HEART_PATH_D}
                fill={`url(#paint_full_${uniqueId})`}
                shapeRendering="crispEdges"
              />
            </g>
            <defs>
              <filter
                id={`filter_full_${uniqueId}`}
                x="0"
                y="-10"
                width="177"
                height="176"
                filterUnits="userSpaceOnUse"
                colorInterpolationFilters="sRGB"
              >
                <feFlood floodOpacity="0" result="BackgroundImageFix" />
                <feColorMatrix
                  in="SourceAlpha"
                  type="matrix"
                  values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                  result="hardAlpha"
                />
                <feOffset dy="5" />
                <feGaussianBlur stdDeviation="5.5" />
                <feComposite in2="hardAlpha" operator="out" />
                <feColorMatrix
                  type="matrix"
                  values="0 0 0 0 1 0 0 0 0 0.376897 0 0 0 0 0.93769 0 0 0 0.26 0"
                />
                <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_full" />
                <feColorMatrix
                  in="SourceAlpha"
                  type="matrix"
                  values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                  result="hardAlpha"
                />
                <feOffset dy="2" />
                <feGaussianBlur stdDeviation="1" />
                <feComposite in2="hardAlpha" operator="out" />
                <feColorMatrix type="matrix" values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.55 0" />
                <feBlend
                  mode="normal"
                  in2="effect1_dropShadow_full"
                  result="effect2_dropShadow_full"
                />
                <feColorMatrix
                  in="SourceAlpha"
                  type="matrix"
                  values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                  result="hardAlpha"
                />
                <feOffset dy="-1" />
                <feGaussianBlur stdDeviation="1.5" />
                <feComposite in2="hardAlpha" operator="out" />
                <feColorMatrix
                  type="matrix"
                  values="0 0 0 0 0.654353 0 0 0 0 0 0 0 0 0 0.28957 0 0 0 0.14 0"
                />
                <feBlend
                  mode="normal"
                  in2="effect2_dropShadow_full"
                  result="effect3_dropShadow_full"
                />
                <feBlend
                  mode="normal"
                  in="SourceGraphic"
                  in2="effect3_dropShadow_full"
                  result="shape"
                />
                <feColorMatrix
                  in="SourceAlpha"
                  type="matrix"
                  values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                  result="hardAlpha"
                />
                <feOffset dy="3" />
                <feGaussianBlur stdDeviation="1.5" />
                <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
                <feColorMatrix type="matrix" values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.35 0" />
                <feBlend mode="normal" in2="shape" result="effect4_innerShadow_full" />
              </filter>
              <linearGradient
                id={`paint_full_${uniqueId}`}
                x1="-117.928"
                y1="48.0221"
                x2="113.114"
                y2="-109.853"
                gradientUnits="userSpaceOnUse"
              >
                <stop stopColor="#FF519E" stopOpacity="0.5" />
                <stop offset="0.3125" stopColor="#FF519E" stopOpacity="0.2" />
                <stop offset="0.40625" stopColor="#FF519E" stopOpacity="0.4" />
                <stop offset="0.5" stopColor="#FF519E" stopOpacity="0.6" />
                <stop offset="1" stopColor="#FF519E" stopOpacity="0.1" />
              </linearGradient>
            </defs>
          </svg>

          <button
            type="button"
            onClick={() => {
              handleConvertEnergy();
              onReceiveClick?.();
            }}
            disabled={isConvertingEnergy}
            onMouseDown={() => setIsPressed(true)}
            onMouseUp={() => setIsPressed(false)}
            onMouseLeave={() => setIsPressed(false)}
            onTouchStart={() => setIsPressed(true)}
            onTouchEnd={() => setIsPressed(false)}
            onTouchCancel={() => setIsPressed(false)}
            className="absolute z-20 top-[50%] left-1/2 -translate-x-1/2 -translate-y-1/2 w-full flex flex-col items-center gap-[6px]"
          >
            <p
              className="text-[17px] font-extrabold leading-[20px] text-pure-white text-center whitespace-pre-line"
              style={{ textShadow: '0 0 4px rgba(255, 0, 0, 0.25)' }}
            >
              {t('BettieFreeCharge.energyHeart.clickAndGetHeart')}
            </p>
            <p className="text-[rgba(170,0,76,0.82)] text-[11px] font-semibold leading-[13px]">
              {t('BettieFreeCharge.energyHeart.currentEnergy', { count })}
            </p>
          </button>
          <p
            ref={plusOneRef}
            className="absolute top-[40%] right-0 text-[28px] font-bold leading-[33px] bg-graident-single-red text-transparent bg-clip-text opacity-0 pointer-events-none"
            aria-hidden="true"
          >
            +1
          </p>
        </>
      ) : (
        <>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 164 152"
            fill="none"
            className="absolute z-0 w-full h-full"
            style={{ overflow: 'visible' }}
            aria-label="Heart Background"
            role="img"
          >
            <defs>
              <filter
                id={`filter-empty-${uniqueId}`}
                x="0"
                y="-1"
                width="164"
                height="153"
                filterUnits="userSpaceOnUse"
                colorInterpolationFilters="sRGB"
              >
                <feFlood floodOpacity="0" result="BackgroundImageFix" />
                <feColorMatrix
                  in="SourceAlpha"
                  type="matrix"
                  values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                  result="hardAlpha"
                />
                <feOffset dy="4" />
                <feGaussianBlur stdDeviation="2" />
                <feComposite in2="hardAlpha" operator="out" />
                <feColorMatrix type="matrix" values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.25 0" />
                <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow" />
                <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow" result="shape" />
                <feColorMatrix
                  in="SourceAlpha"
                  type="matrix"
                  values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                  result="hardAlpha"
                />
                <feOffset dy="6" />
                <feGaussianBlur stdDeviation="5" />
                <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
                <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.08 0" />
                <feBlend mode="normal" in2="shape" result="effect2_innerShadow" />
                <feColorMatrix
                  in="SourceAlpha"
                  type="matrix"
                  values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                  result="hardAlpha"
                />
                <feOffset dy="4" />
                <feGaussianBlur stdDeviation="2.5" />
                <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
                <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.15 0" />
                <feBlend mode="normal" in2="effect2_innerShadow" result="effect3_innerShadow" />
                <feColorMatrix
                  in="SourceAlpha"
                  type="matrix"
                  values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                  result="hardAlpha"
                />
                <feOffset dy="-1" />
                <feGaussianBlur stdDeviation="1" />
                <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
                <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.02 0" />
                <feBlend mode="normal" in2="effect3_innerShadow" result="effect4_innerShadow" />
              </filter>
            </defs>
            <g filter={`url(#filter-empty-${uniqueId})`}>
              <path d={HEART_PATH_D} fill={colorMode === 'dark' ? '#303030' : '#EEEEEE'} />
            </g>
          </svg>

          {levelImageSrc && (
            <div
              className={`absolute left-0 right-0 z-10 flex items-center justify-center ${getLevelClass(safeCount)}`}
            >
              <img src={levelImageSrc} alt={`Energy level ${safeCount}`} />
            </div>
          )}

          <div className="absolute top-[45%] left-1/2 -translate-x-1/2 -translate-y-1/2 w-full flex items-center justify-center z-20 pointer-events-none">
            <span className="text-[14px] font-medium text-text-main-lv3 dark:text-text-main-lv2 leading-[18px]">
              {t('BettieFreeCharge.energyHeart.energyPerPoint', { count: energyPerPoint })}
            </span>
          </div>
        </>
      )}
    </div>
  );
};

export default EnergyHeart;
